import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, Copy, ShieldCheck, User, Mail, Repeat } from "lucide-react";

export default function Members() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showToken, setShowToken] = useState(null); // { email, plaintext_password }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "member", password: "", reports_to: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const { data } = await api.post("/users", payload);
      toast.success(`${data.name} created`);
      setShowToken({ email: data.email, password: data.plaintext_password, name: data.name });
      setShowCreate(false);
      setForm({ name: "", email: "", role: "member", password: "", reports_to: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const regenerate = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/regenerate-password`);
      setShowToken({ email: data.email, password: data.plaintext_password, name: u.name });
      toast.success("Password regenerated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setReportsTo = async (u, mgr_id) => {
    try {
      await api.patch(`/users/${u.id}`, { reports_to: mgr_id === "__none__" ? "" : mgr_id });
      toast.success("Reporting line updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setUserRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.name} is now a ${role}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const managers = users.filter((u) => u.role === "manager" && u.active);

  const doDelete = async () => {
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success("Member removed");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(t);
      toast.success("Copied");
    } catch {
      toast.error("Copy blocked — please copy manually");
    }
  };

  return (
    <div className="space-y-8" data-testid="members-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">SuperAdmin</p>
          <h1 className="text-base font-semibold font-semibold mt-2">Team Members</h1>
          <p className="text-slate-600 mt-2">Create login tokens and manage access for your sales team.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white font-semibold" data-testid="create-member-btn">
          <UserPlus className="w-4 h-4 mr-2" /> New Member
        </Button>
      </header>

      <div className="solid-panel overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-100/70 text-slate-600 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-6 py-4">Member</th>
              <th className="text-left px-6 py-4">Role</th>
              <th className="text-left px-6 py-4">Status</th>
              <th className="text-left px-6 py-4">Permissions</th>
              <th className="text-left px-6 py-4">Created</th>
              <th className="text-right px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">No members yet</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-200/60 hover:bg-white/60" data-testid={`member-row-${u.email}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100/70 border border-slate-200 flex items-center justify-center flex-shrink-0">
                      {u.role === "superadmin" ? <ShieldCheck className="w-4 h-4 text-[#1D63ED]" /> : <User className="w-4 h-4 text-slate-700" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Select value={u.role} onValueChange={(v) => setUserRole(u, v)}>
                    <SelectTrigger className="w-32 h-7 text-xs bg-slate-50 border-slate-200" data-testid={`role-select-${u.email}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="superadmin">SuperAdmin</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-6 py-4">
                  <button onClick={() => toggleActive(u)} className={`text-xs px-2 py-0.5 rounded-full border font-medium ${u.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`} data-testid={`toggle-active-${u.email}`}>
                    {u.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-6 py-4">
                  {u.role === "superadmin" ? (
                    <span className="text-[11px] text-slate-400 italic">All access</span>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={u.reports_to || "__none__"} onValueChange={(v) => setReportsTo(u, v)}>
                        <SelectTrigger className="w-40 h-7 text-xs bg-slate-50 border-slate-200" data-testid={`reports-to-${u.email}`}>
                          <SelectValue placeholder="Reports to…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— None —</SelectItem>
                          {managers.filter((m) => m.id !== u.id).map((m) => (
                            <SelectItem key={m.id} value={m.id}>Reports to {m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-slate-600 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-right">
                  <div className="inline-flex flex-wrap gap-1 justify-end">
                  <Button variant="ghost" size="sm" onClick={async () => {
                    try {
                      const { data } = await api.post(`/reminders/send-user/${u.id}`);
                      if (data.sent) toast.success(`Reminder emailed to ${u.name}`);
                      else if (data.reason === "throttled") toast.info(`${u.name} was emailed recently — retry in ${data.retry_after_minutes} min`);
                      else if (data.reason === "no items") toast.info(`${u.name} has no items to remind`);
                      else toast.error(`Failed to email ${u.name}`);
                    } catch (e) { toast.error(formatApiError(e)); }
                  }} data-testid={`email-${u.email}`}>
                    <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => regenerate(u)} data-testid={`regen-${u.email}`}>
                    <KeyRound className="w-3.5 h-3.5 mr-1.5" /> New token
                  </Button>
                  {u.role !== "superadmin" && (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(u)} className="text-red-400 hover:text-red-300" data-testid={`delete-${u.email}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="glass-heavy border-slate-200" data-testid="create-member-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Create Member</DialogTitle>
            <DialogDescription>Generate credentials for a new sales team member.</DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreate} className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ravi Sharma" className="mt-1.5 bg-slate-100/70" data-testid="member-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ravi@cerf.com" className="mt-1.5 bg-slate-100/70" data-testid="member-email-input" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5 bg-slate-100/70" data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member (Sales)</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="superadmin">SuperAdmin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "member" && managers.length > 0 && (
              <div>
                <Label>Reports To (optional)</Label>
                <Select value={form.reports_to || "__none__"} onValueChange={(v) => setForm({ ...form, reports_to: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-1.5 bg-slate-100/70" data-testid="member-reports-to-select"><SelectValue placeholder="Pick a manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Custom Password (optional)</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Leave blank to auto-generate" className="mt-1.5 bg-slate-100/70 font-mono" data-testid="member-password-input" />
              <p className="text-xs text-slate-500 mt-1">Auto-generated tokens are 12 characters (letters + digits).</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="submit-create-member">Create & Generate Token</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Show token modal */}
      <Dialog open={!!showToken} onOpenChange={(v) => !v && setShowToken(null)}>
        <DialogContent className="glass-heavy border-slate-200" data-testid="show-token-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              <KeyRound className="text-[#1D63ED]" /> Login Token
            </DialogTitle>
            <DialogDescription>Share these credentials with {showToken?.name} — shown only once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="solid-panel p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-widest">Email</p>
                <p className="font-mono text-sm mt-1">{showToken?.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copy(showToken.email)}><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="solid-panel p-4 flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs text-slate-500 uppercase tracking-widest">Password / Token</p>
                <p className="font-mono text-lg mt-1 text-[#1D63ED]" data-testid="token-value">{showToken?.password}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copy(showToken.password)} data-testid="copy-token-btn"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowToken(null)} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="close-token-dialog">I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="glass-heavy border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke their access. Leads assigned to them will become unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
